/**
 * CashflowConfigPage — Data source selection, forecast assumptions,
 * facility configuration, and data upload history.
 */

import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '../../components/ui';
import {
  useDataLoads,
  useFacilityConfig,
  useAssumptions,
  useUpdateAssumption,
  useDataSourceConfig,
  useAvailableConnectors,
  useSetDataSource,
  useTestConnection,
} from './api';
import { formatCurrency, sortData } from './utils';
import { cn } from '../../shared/lib/cn';
import { SortableHeader } from './SortableHeader';
import type { ForecastAssumption, ConnectorOption } from './types';

type Tab = 'data-source' | 'assumptions' | 'facility' | 'uploads';

function AssumptionRow({ assumption, onSave }: { assumption: ForecastAssumption; onSave: (v: number) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(assumption.assumption_value ?? assumption.assumption_text ?? ''));

  const handleSave = () => {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      onSave(num);
    }
    setEditing(false);
  };

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-dark-surface-2/50">
      <td className="py-2 px-3 text-sm text-charcoal dark:text-gray-200 font-medium">{assumption.assumption_key}</td>
      <td className="py-2 px-3 text-sm text-gray-500 dark:text-gray-400">{assumption.description || '—'}</td>
      <td className="py-2 px-3 text-right">
        {editing ? (
          <div className="flex items-center gap-1 justify-end">
            <input
              type="text"
              value={value}
              onChange={e => setValue(e.target.value)}
              className="w-24 text-sm text-right border border-gray-300 dark:border-dark-border rounded px-2 py-0.5 bg-white dark:bg-dark-surface"
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleSave()}
            />
            <button onClick={handleSave} className="text-xs text-emerald-600 hover:underline">Save</button>
            <button onClick={() => setEditing(false)} className="text-xs text-gray-400 hover:underline">Cancel</button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="text-sm tabular-nums font-mono text-charcoal dark:text-gray-200 hover:text-eliza-red"
          >
            {assumption.assumption_value ?? assumption.assumption_text ?? '—'}
          </button>
        )}
      </td>
      <td className="py-2 px-3 text-xs text-gray-400">{assumption.updated_by || '—'}</td>
    </tr>
  );
}

function DataSourceTab() {
  const { data: config, isLoading: configLoading } = useDataSourceConfig();
  const { data: connectors, isLoading: connectorsLoading } = useAvailableConnectors();
  const setDataSource = useSetDataSource();
  const testConn = useTestConnection();
  const [selectedConnector, setSelectedConnector] = useState('');
  const [schema, setSchema] = useState('cashflow');
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const handleTest = () => {
    if (!selectedConnector) return;
    setTestResult(null);
    testConn.mutate(
      { connector_id: selectedConnector, redshift_schema: schema },
      {
        onSuccess: (data) => setTestResult({ ok: data.redshift_connected, msg: data.message }),
        onError: (err) => setTestResult({ ok: false, msg: err.message }),
      },
    );
  };

  const handleSave = () => {
    if (!selectedConnector) return;
    setDataSource.mutate({ connector_id: selectedConnector, redshift_schema: schema });
  };

  return (
    <div className="space-y-6">
      {/* Current Status */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100 mb-4">Current Data Source</h3>
          {configLoading ? (
            <p className="text-sm text-gray-400">Loading...</p>
          ) : config?.is_connected ? (
            <div className="flex items-center gap-3">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
              <div>
                <p className="text-sm font-medium text-charcoal dark:text-gray-200">{config.connector_name}</p>
                <p className="text-xs text-gray-500">
                  {config.connector_type} &middot; Schema: <code className="bg-gray-100 dark:bg-dark-surface-2 px-1 rounded">{config.redshift_schema}</code>
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="w-2.5 h-2.5 rounded-full bg-gray-300 inline-block" />
              <p className="text-sm text-gray-500">No data source configured. Select a Redshift connector below.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Connector Selection */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100 mb-4">Select Redshift Connector</h3>
          {connectorsLoading ? (
            <p className="text-sm text-gray-400">Loading connectors...</p>
          ) : !connectors?.length ? (
            <div className="text-sm text-gray-500 space-y-3">
              <p>No Redshift connectors available yet.</p>
              <p className="text-xs text-gray-400">
                Go to <strong>Data Connections</strong> → <strong>New Source Connection</strong> → <strong>Amazon Redshift</strong> to
                create a connector using your AWS IAM credentials (Access Key &amp; Secret Key).
              </p>
              <a
                href="/data-connections"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-eliza-red border border-eliza-red/30 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
              >
                Go to Data Connections
              </a>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {connectors.map((c: ConnectorOption) => (
                  <button
                    key={c.connector_id}
                    onClick={() => setSelectedConnector(c.connector_id)}
                    className={cn(
                      'text-left p-3 rounded-lg border transition-colors',
                      selectedConnector === c.connector_id
                        ? 'border-eliza-red bg-red-50 dark:bg-red-950/20'
                        : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-600',
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={cn('w-2 h-2 rounded-full', c.is_healthy ? 'bg-emerald-500' : 'bg-red-400')} />
                      <span className="text-sm font-medium text-charcoal dark:text-gray-200">{c.connector_name}</span>
                    </div>
                    <p className="text-xs text-gray-500">{c.connector_id}</p>
                  </button>
                ))}
              </div>

              {selectedConnector && (
                <div className="space-y-3 pt-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Redshift Schema</label>
                    <input
                      type="text"
                      value={schema}
                      onChange={(e) => setSchema(e.target.value)}
                      className="w-48 text-sm border border-gray-300 dark:border-dark-border rounded px-3 py-1.5 bg-white dark:bg-dark-surface"
                      placeholder="cashflow"
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleTest}
                      disabled={testConn.isPending}
                      className="px-4 py-1.5 text-sm font-medium border border-gray-300 dark:border-dark-border rounded hover:bg-gray-50 dark:hover:bg-dark-surface-2 disabled:opacity-50"
                    >
                      {testConn.isPending ? 'Testing...' : 'Test Connection'}
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={setDataSource.isPending}
                      className="px-4 py-1.5 text-sm font-medium bg-eliza-red text-white rounded hover:bg-red-700 disabled:opacity-50"
                    >
                      {setDataSource.isPending ? 'Saving...' : 'Save & Activate'}
                    </button>
                  </div>

                  {testResult && (
                    <div className={cn(
                      'p-3 rounded text-sm',
                      testResult.ok ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400' : 'bg-red-50 text-red-700 dark:bg-red-950/20 dark:text-red-400',
                    )}>
                      {testResult.ok ? '✓ Connection successful' : `✗ ${testResult.msg}`}
                    </div>
                  )}

                  {setDataSource.isSuccess && (
                    <div className="p-3 rounded text-sm bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400">
                      ✓ Data source configured. All cashflow queries will now use this connector.
                    </div>
                  )}

                  {setDataSource.isError && (
                    <div className="p-3 rounded text-sm bg-red-50 text-red-700 dark:bg-red-950/20 dark:text-red-400">
                      ✗ {setDataSource.error?.message || 'Failed to save'}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState<Tab>('data-source');
  const [dlSortBy, setDlSortBy] = useState('load_date');
  const [dlSortDir, setDlSortDir] = useState('DESC');
  const { data: dataLoads } = useDataLoads();
  const { data: facility } = useFacilityConfig();
  const { data: assumptions } = useAssumptions();
  const updateAssumption = useUpdateAssumption();

  const handleDlSort = (field: string) => {
    if (dlSortBy === field) {
      setDlSortDir(d => d === 'DESC' ? 'ASC' : 'DESC');
    } else {
      setDlSortBy(field);
      setDlSortDir('ASC');
    }
  };

  const sortedDataLoads = useMemo(
    () => sortData(dataLoads ?? [], dlSortBy, dlSortDir),
    [dataLoads, dlSortBy, dlSortDir],
  );

  const categories = assumptions
    ? Array.from(new Set(assumptions.map(a => a.category)))
    : [];

  const tabLabels: Record<Tab, string> = {
    'data-source': 'Data Source',
    assumptions: 'Forecast Assumptions',
    facility: 'Facility Config',
    uploads: 'Data Uploads',
  };

  return (
    <div className="space-y-6 mt-2">
      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 dark:border-dark-border">
        {(['data-source', 'assumptions', 'facility', 'uploads'] as Tab[]).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab
                ? 'border-eliza-red text-eliza-red'
                : 'border-transparent text-gray-500 hover:text-charcoal dark:text-gray-400 dark:hover:text-gray-200',
            )}
          >
            {tabLabels[tab]}
          </button>
        ))}
      </div>

      {/* Data Source Tab */}
      {activeTab === 'data-source' && <DataSourceTab />}

      {/* Assumptions Tab */}
      {activeTab === 'assumptions' && (
        <div className="space-y-6">
          {categories.map(cat => {
            const items = assumptions!.filter(a => a.category === cat);
            return (
              <Card key={cat}>
                <CardContent className="p-0">
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-border">
                    <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100 capitalize">
                      {cat.replace(/_/g, ' ')}
                    </h3>
                  </div>
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 dark:border-dark-border/50">
                        <th className="text-left py-2 px-3 font-medium text-gray-500 w-40">Key</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-500">Description</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-500 w-32">Value</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-500 w-24">Updated By</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-dark-border/50">
                      {items.map(a => (
                        <AssumptionRow
                          key={`${a.category}-${a.assumption_key}`}
                          assumption={a}
                          onSave={v => updateAssumption.mutate({ category: a.category, key: a.assumption_key, value: v })}
                        />
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Facility Config Tab */}
      {activeTab === 'facility' && (
        <Card>
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100 mb-4">Credit Facility</h3>
            {facility ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {[
                  { label: 'Total Commitment', value: formatCurrency(facility.total_commitment) },
                  { label: 'Current Outstanding', value: formatCurrency(facility.current_outstanding) },
                  { label: 'Minimum Cash Covenant', value: formatCurrency(facility.minimum_cash_covenant) },
                  { label: 'Max Utilization', value: `${facility.maximum_utilization_pct}%` },
                  { label: 'Draw Minimum', value: formatCurrency(facility.draw_minimum) },
                  { label: 'Approval Threshold', value: formatCurrency(facility.draw_approval_threshold) },
                ].map(item => (
                  <div key={item.label}>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{item.label}</p>
                    <p className="text-sm font-medium text-charcoal dark:text-gray-200">{item.value}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No facility configuration found.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Data Uploads Tab */}
      {activeTab === 'uploads' && (
        <Card>
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-border flex items-center justify-between">
              <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">Data Load History</h3>
            </div>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-surface-2">
                  <SortableHeader label="Type" field="load_type" sortBy={dlSortBy} sortDir={dlSortDir} onSort={handleDlSort} />
                  <SortableHeader label="Filename" field="source_filename" sortBy={dlSortBy} sortDir={dlSortDir} onSort={handleDlSort} />
                  <SortableHeader label="Status" field="validation_status" sortBy={dlSortBy} sortDir={dlSortDir} onSort={handleDlSort} />
                  <SortableHeader label="Records" field="record_count" sortBy={dlSortBy} sortDir={dlSortDir} onSort={handleDlSort} align="right" />
                  <SortableHeader label="Date" field="load_date" sortBy={dlSortBy} sortDir={dlSortDir} onSort={handleDlSort} />
                  <SortableHeader label="Loaded By" field="created_by" sortBy={dlSortBy} sortDir={dlSortDir} onSort={handleDlSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-dark-border/50">
                {!sortedDataLoads.length ? (
                  <tr><td colSpan={6} className="text-center py-8 text-gray-400">No data loads</td></tr>
                ) : sortedDataLoads.map(dl => (
                  <tr key={dl.id} className="hover:bg-gray-50 dark:hover:bg-dark-surface-2/50">
                    <td className="py-2 px-3 font-medium text-charcoal dark:text-gray-200">{dl.load_type}</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{dl.source_filename || dl.source || '—'}</td>
                    <td className="py-2 px-3">
                      <span className={cn(
                        'px-2 py-0.5 rounded text-xs font-medium',
                        dl.validation_status === 'PASS' ? 'bg-emerald-100 text-emerald-700' :
                        dl.validation_status === 'FAIL' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700',
                      )}>
                        {dl.validation_status}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums">{dl.record_count ?? '—'}</td>
                    <td className="py-2 px-3 text-gray-500">{dl.load_date}</td>
                    <td className="py-2 px-3 text-gray-500">{dl.created_by || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
